import { Code, Zap, Sparkles, Rocket, Shield, Brain, TrendingUp, LucideIcon } from "lucide-react";

export interface PricingPackage {
  name: string;
  price: string;
  description: string;
  features: string[];
  color: string;
  icon: LucideIcon;
  popular?: boolean;
}

export interface DigitalService {
  name: string;
  price: string;
  description: string;
}

export interface MonthlyService {
  name: string;
  price: string;
  features: string[];
  color: string;
  popular?: boolean;
}

export const websitePackages: PricingPackage[] = [
  {
    name: "Starter Website",
    price: "250€",
    description: "Perfect for small businesses and personal brands",
    features: [
      "1–3 pages",
      "Modern responsive design",
      "Contact form",
      "Basic SEO setup",
      "Mobile optimized",
      "Fast delivery (1 week)"
    ],
    color: "from-violet-500 to-purple-500",
    icon: Code,
  },
  {
    name: "Business Website",
    price: "600€",
    description: "Complete solution for growing businesses",
    features: [
      "Up to 7 pages",
      "Custom UI design",
      "SEO structure",
      "Speed optimized",
      "Admin panel",
      "2 revisions",
      "Analytics integration"
    ],
    popular: true,
    color: "from-fuchsia-500 to-pink-500",
    icon: Zap,
  },
  {
    name: "Professional Website",
    price: "1,200€",
    description: "Premium experience for established brands",
    features: [
      "Custom design system",
      "Advanced animations",
      "Multi-language support",
      "Blog setup",
      "Technical SEO",
      "Performance optimization",
      "30 days support"
    ],
    color: "from-cyan-500 to-blue-500",
    icon: Sparkles,
  },
];

export const webAppPackages: PricingPackage[] = [
  {
    name: "Basic Web App",
    price: "800€",
    description: "Simple web applications with core functionality",
    features: [
      "User authentication",
      "Dashboard interface",
      "Basic database logic",
      "Responsive design",
      "Secure & tested"
    ],
    color: "from-violet-500 to-purple-500",
    icon: Code,
  },
  {
    name: "Advanced Web App",
    price: "1,500€",
    description: "Full-featured business applications",
    features: [
      "Custom backend",
      "API integrations",
      "Admin roles & permissions",
      "Scalable architecture",
      "Database design",
      "Deployment included"
    ],
    popular: true,
    color: "from-fuchsia-500 to-pink-500",
    icon: Rocket,
  },
  {
    name: "Enterprise System",
    price: "3,000€+",
    description: "Complex systems built for scale",
    features: [
      "Full architecture planning",
      "Automation systems",
      "Cloud deployment",
      "Security hardening",
      "Multi-tier infrastructure",
      "Ongoing consultation"
    ],
    color: "from-cyan-500 to-blue-500",
    icon: Shield,
  },
];

export const aiAutomation: PricingPackage[] = [
  {
    name: "AI Chatbot",
    price: "400€",
    description: "Intelligent customer support",
    features: [
      "Website integration",
      "Knowledge base training",
      "Basic customization",
      "24/7 availability"
    ],
    color: "from-violet-500 to-purple-500",
    icon: Brain,
  },
  {
    name: "Business Automation",
    price: "700€",
    description: "Streamline your operations",
    features: [
      "CRM automation",
      "Email automation",
      "API integrations",
      "Workflow optimization"
    ],
    popular: true,
    color: "from-fuchsia-500 to-pink-500",
    icon: Zap,
  },
  {
    name: "Custom AI Tool",
    price: "1,500€+",
    description: "Tailored AI solutions",
    features: [
      "Custom backend logic",
      "User interface design",
      "Cloud deployment",
      "Performance optimization",
      "Training & documentation"
    ],
    color: "from-cyan-500 to-blue-500",
    icon: Sparkles,
  },
];

export const digitalServices: DigitalService[] = [
  { name: "SEO Setup", price: "200€", description: "Get found on Google" },
  { name: "Technical SEO Audit", price: "300€", description: "In-depth analysis & recommendations" },
  { name: "Full SEO Optimization", price: "700€", description: "Complete search engine strategy" },
  { name: "Website Speed Optimization", price: "150€", description: "Lightning-fast performance" },
];

export const monthlyServices: MonthlyService[] = [
  {
    name: "Maintenance Plan",
    price: "80€/month",
    features: ["Updates & patches", "Security monitoring", "Backup management", "Bug fixes"],
    color: "from-violet-500 to-fuchsia-500",
  },
  {
    name: "Hosting & Monitoring",
    price: "40€/month",
    features: ["Reliable hosting", "Uptime monitoring", "SSL certificate", "Email support"],
    color: "from-fuchsia-500 to-cyan-500",
  },
  {
    name: "Growth & Optimization",
    price: "150€/month",
    features: ["Performance tuning", "SEO improvements", "Analytics reports", "Strategic consulting"],
    color: "from-cyan-500 to-violet-500",
    popular: true,
  },
];